import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const KEY = "ajc-theme";
const ThemeContext = createContext(null);

/** Marketing and auth pages have their own fixed palette and never follow the app theme. */
const isAppRoute = (path) => path.startsWith("/app") || path.startsWith("/owner");

const systemDark = () => window.matchMedia?.("(prefers-color-scheme: dark)").matches;

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem(KEY) || "light");
  const [dark, setDark] = useState(false);
  const { pathname } = useLocation();
  const isPublic = !isAppRoute(pathname);

  useEffect(() => { localStorage.setItem(KEY, mode); }, [mode]);

  useEffect(() => {
    const el = document.documentElement;
    const apply = () => {
      const on = !isPublic && (mode === "dark" || (mode === "system" && systemDark()));
      el.classList.toggle("dark", on);
      el.style.colorScheme = on ? "dark" : "light";
      setDark(on);
    };
    apply();
    if (mode !== "system" || isPublic) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [mode, isPublic]);

  const value = useMemo(() => ({ mode, setMode, dark }), [mode, dark]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext) || { mode: "light", setMode: () => {}, dark: false };

/** Retained for pages that opt out explicitly; the provider already handles public routes. */
export const useForceLightSurface = () => {};
